import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import PendingRecipesList from '../components/PendingRecipesList';
import { Factory, Plus, Calendar, Package, AlertTriangle, Trash2, Edit } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ProductionPage = () => {
  const { getAuthHeaders } = useAuth();
  const [productions, setProductions] = useState([]);
  const [pendingRecipes, setPendingRecipes] = useState([]);
  const [recipeTemplates, setRecipeTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [validating, setValidating] = useState(false);
  const [showNewProductionModal, setShowNewProductionModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [productionToDelete, setProductionToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  
  // Edit production state
  const [showEditProductionModal, setShowEditProductionModal] = useState(false);
  const [editingProduction, setEditingProduction] = useState(null);
  const [editForm, setEditForm] = useState({
    date: '',
    aggloType: '',
    aggloCount: ''
  });
  const [updating, setUpdating] = useState(false);
  
  // Simplified form state - just type and quantity
  const [newProduction, setNewProduction] = useState({
    date: new Date().toISOString().split('T')[0],
    aggloType: 'agglo_10',
    aggloCount: ''
  });

  // Selected template based on agglo type
  const [selectedTemplate, setSelectedTemplate] = useState(null);

  useEffect(() => {
    fetchAll();
  }, []);

  const fetchAll = async () => {
    setLoading(true);
    await Promise.all([
      fetchProductions(),
      fetchPendingRecipes(),
      fetchRecipeTemplates()
    ]);
    setLoading(false);
  };

  const fetchRecipeTemplates = async () => {
    try {
      const response = await axios.get(`${API}/recipe-templates`, getAuthHeaders());
      setRecipeTemplates(response.data);
    } catch (error) {
      console.error('Error fetching recipe templates:', error);
    }
  };

  const fetchProductions = async () => {
    try {
      const response = await axios.get(`${API}/production`, getAuthHeaders());
      setProductions(response.data);
    } catch (error) {
      console.error('Error fetching productions:', error);
      toast.error('Erreur lors du chargement des productions');
    }
  };

  const fetchPendingRecipes = async () => {
    try {
      const response = await axios.get(`${API}/recipes?status=pending`, getAuthHeaders());
      setPendingRecipes(response.data);
    } catch (error) {
      console.error('Error fetching pending recipes:', error);
    }
  };

  const handleValidateDailyProduction = async () => {
    setValidating(true);
    try {
      await axios.post(
        `${API}/production/validate-daily`,
        {},
        getAuthHeaders()
      );
      toast.success('Production journalière validée ! Stocks mis à jour.');
      setPendingRecipes([]);
      fetchProductions();
    } catch (error) {
      console.error('Error validating production:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de la validation');
    } finally {
      setValidating(false);
    }
  };

  const handleDeleteRecipe = async (recipeId) => {
    try {
      await axios.delete(`${API}/recipes/${recipeId}`, getAuthHeaders());
      toast.success('Recette supprimée');
      fetchPendingRecipes();
    } catch (error) {
      console.error('Error deleting recipe:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de la suppression');
    }
  };

  const resetNewProductionForm = () => {
    setNewProduction({
      date: new Date().toISOString().split('T')[0],
      aggloType: 'agglo_10',
      aggloCount: ''
    });
    setSelectedTemplate(null);
  };

  // Update selected template when agglo type changes
  const handleAggloTypeChange = (value) => {
    setNewProduction({...newProduction, aggloType: value});
    const template = recipeTemplates.find(t => t.agglo_type === value);
    setSelectedTemplate(template || null);
  };

  // Open modal and set initial template
  const openNewProductionModal = () => {
    setShowNewProductionModal(true);
    const template = recipeTemplates.find(t => t.agglo_type === 'agglo_10');
    setSelectedTemplate(template || null);
  };

  const handleCreateNewProduction = async () => {
    if (!newProduction.aggloCount || parseInt(newProduction.aggloCount) <= 0) {
      toast.error('Veuillez saisir le nombre d\'agglos produits');
      return;
    }

    if (!selectedTemplate) {
      toast.error('Aucune recette de référence trouvée pour ce type d\'agglo. Créez d\'abord une recette depuis le calculateur.');
      return;
    }

    setSaving(true);
    try {
      await axios.post(
        `${API}/production/quick`,
        {
          agglo_type: newProduction.aggloType,
          agglo_count: parseInt(newProduction.aggloCount),
          production_date: newProduction.date
        },
        getAuthHeaders()
      );

      toast.success(`Production créée ! ${newProduction.aggloCount} ${getAggloTypeLabel(newProduction.aggloType)} ajoutés au stock.`);
      setShowNewProductionModal(false);
      resetNewProductionForm();
      fetchProductions();
    } catch (error) {
      console.error('Error creating production:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de la création de la production');
    } finally {
      setSaving(false);
    }
  };

  // Calculate estimated materials based on template and quantity
  const getEstimatedMaterials = () => {
    if (!selectedTemplate || !newProduction.aggloCount) return null;
    const count = parseInt(newProduction.aggloCount) || 0;
    return {
      cement: (selectedTemplate.cement_per_unit * count).toFixed(1),
      sand: (selectedTemplate.sand_per_unit * count).toFixed(1),
      duneSand: (selectedTemplate.dune_sand_per_unit * count).toFixed(1),
      gravel: (selectedTemplate.gravel_per_unit * count).toFixed(1),
      totalCost: (selectedTemplate.cost_per_unit * count).toFixed(0)
    };
  };

  // Delete production
  const handleDeleteProduction = async () => {
    if (!productionToDelete) return;
    
    setDeleting(true);
    try {
      await axios.delete(
        `${API}/production/${productionToDelete.id}`,
        getAuthHeaders()
      );
      toast.success('Production supprimée et stocks restaurés');
      setProductionToDelete(null);
      fetchProductions();
    } catch (error) {
      console.error('Error deleting production:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de la suppression');
    } finally {
      setDeleting(false);
    }
  };

  // Open edit modal
  const openEditModal = (production) => {
    setEditingProduction(production);
    setEditForm({
      date: production.production_date || new Date(production.created_at).toISOString().split('T')[0],
      aggloType: production.agglo_type,
      aggloCount: production.agglo_produced.toString()
    });
    setShowEditProductionModal(true);
  };

  // Handle update production
  const handleUpdateProduction = async () => {
    if (!editForm.aggloCount || parseInt(editForm.aggloCount) <= 0) {
      toast.error('Veuillez saisir une quantité valide');
      return;
    }

    setUpdating(true);
    try {
      await axios.put(
        `${API}/production/${editingProduction.id}`,
        {
          agglo_type: editForm.aggloType,
          agglo_produced: parseInt(editForm.aggloCount),
          production_date: editForm.date
        },
        getAuthHeaders()
      );
      toast.success('Production mise à jour');
      setShowEditProductionModal(false);
      setEditingProduction(null);
      fetchProductions();
    } catch (error) {
      console.error('Error updating production:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de la mise à jour');
    } finally {
      setUpdating(false);
    }
  };

  const getAggloTypeLabel = (type) => {
    const labels = {
      'agglo_10': 'Agglo de 10',
      'agglo_15': 'Agglo de 15',
      'agglo_20': 'Agglo de 20',
      'agglo_hourdis_16': 'Agglo Hourdis 16',
      'agglo_plein_15': 'Agglo Plein 15',
      'mixed': 'Mixte'
    };
    return labels[type] || type;
  };

  const getCementTypeLabel = (type) => {
    const labels = {
      'ciment_42_5': 'Ciment 42.5'
    };
    return labels[type] || type;
  };

  const estimatedMaterials = getEstimatedMaterials();

  if (loading) {
    return <div className="flex items-center justify-center h-full">Chargement...</div>;
  }

  return (
    <div className="space-y-6" data-testid="production-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-4xl font-bold text-[#1B3268] tracking-tight flex items-center gap-3" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
            <Factory className="w-10 h-10" />
            Production Journalière
          </h1>
          <p className="text-sm text-slate-600 mt-1">Enregistrez une nouvelle production ou validez les recettes en attente</p>
        </div>
        <Button
          onClick={openNewProductionModal}
          data-testid="new-production-button"
          className="bg-[#1B3268] hover:bg-[#152a55] text-white font-bold rounded-sm shadow-lg h-12 px-6"
        >
          <Plus className="w-5 h-5 mr-2" />
          Nouvelle Production
        </Button>
      </div>

      {/* Pending Recipes for Daily Production */}
      <PendingRecipesList 
        recipes={pendingRecipes} 
        onValidateAll={handleValidateDailyProduction}
        onDeleteRecipe={handleDeleteRecipe}
        validating={validating}
      />

      {/* Productions Table */}
      <Card className="bg-white border border-slate-200 rounded-sm shadow-sm overflow-hidden" data-testid="productions-table">
        <div className="border-b border-slate-100 p-4 bg-slate-50/50">
          <h3 className="text-xl font-semibold text-[#1B3268]">Historique des Productions</h3>
          <p className="text-sm text-slate-500 mt-1">Détails des matériaux utilisés et coûts par production</p>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-slate-100 text-slate-700 font-semibold uppercase text-xs tracking-wider">
                <th className="p-4 text-left">Date</th>
                <th className="p-4 text-left">Type d'Agglo</th>
                <th className="p-4 text-right">Quantité</th>
                <th className="p-4 text-left">Matériaux Utilisés</th>
                <th className="p-4 text-right">Coût Total</th>
                <th className="p-4 text-right">Coût Unitaire</th>
                <th className="p-4 text-center">Actions</th>
              </tr>
            </thead>
            <tbody>
              {productions.map((prod) => (
                <tr key={prod.id} className="hover:bg-slate-50 transition-colors border-b last:border-0" data-testid={`production-row-${prod.id}`}>
                  <td className="p-4">
                    <div className="flex items-center gap-2">
                      <Calendar className="w-4 h-4 text-slate-400" />
                      <span className="font-medium">
                        {prod.production_date 
                          ? new Date(prod.production_date).toLocaleDateString('fr-FR', {
                              day: '2-digit',
                              month: '2-digit',
                              year: 'numeric'
                            })
                          : new Date(prod.created_at).toLocaleDateString('fr-FR', {
                              day: '2-digit',
                              month: '2-digit',
                              year: 'numeric'
                            })
                        }
                      </span>
                    </div>
                  </td>
                  <td className="p-4">
                    <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold ${
                      prod.agglo_type === 'agglo_10' ? 'bg-blue-100 text-blue-800' :
                      prod.agglo_type === 'agglo_15' ? 'bg-green-100 text-green-800' :
                      prod.agglo_type === 'agglo_20' ? 'bg-purple-100 text-purple-800' :
                      prod.agglo_type === 'agglo_hourdis_16' ? 'bg-orange-100 text-orange-800' :
                      prod.agglo_type === 'agglo_plein_15' ? 'bg-pink-100 text-pink-800' :
                      'bg-yellow-100 text-yellow-800'
                    }`}>
                      {getAggloTypeLabel(prod.agglo_type)}
                    </span>
                  </td>
                  <td className="p-4 text-right">
                    <span className="text-2xl font-bold text-[#1B3268]">{prod.agglo_produced}</span>
                    <span className="text-xs text-slate-500 ml-1">unités</span>
                  </td>
                  <td className="p-4">
                    <div className="space-y-1 text-sm">
                      <div className="flex justify-between items-center bg-slate-50 px-2 py-1 rounded">
                        <span className="text-slate-600">{getCementTypeLabel(prod.cement_type)}</span>
                        <span className="font-mono font-medium">{prod.cement_used_kg.toFixed(1)} kg</span>
                      </div>
                      <div className="flex justify-between items-center bg-slate-50 px-2 py-1 rounded">
                        <span className="text-slate-600">Sable</span>
                        <span className="font-mono font-medium">{prod.sand_used_kg.toFixed(1)} kg</span>
                      </div>
                      {prod.dune_sand_used_kg > 0 && (
                        <div className="flex justify-between items-center bg-slate-50 px-2 py-1 rounded">
                          <span className="text-slate-600">Gravier 2/4</span>
                          <span className="font-mono font-medium">{prod.dune_sand_used_kg.toFixed(1)} kg</span>
                        </div>
                      )}
                      <div className="flex justify-between items-center bg-slate-50 px-2 py-1 rounded">
                        <span className="text-slate-600">Gravier 3/8</span>
                        <span className="font-mono font-medium">{prod.gravel_used_kg.toFixed(1)} kg</span>
                      </div>
                    </div>
                  </td>
                  <td className="p-4 text-right">
                    <div className="text-lg font-bold text-red-600">
                      {prod.total_cost.toLocaleString('fr-FR')}
                    </div>
                    <div className="text-xs text-slate-500">FCFA</div>
                  </td>
                  <td className="p-4 text-right">
                    <div className="text-lg font-bold text-green-600">
                      {prod.unit_cost.toFixed(0)}
                    </div>
                    <div className="text-xs text-slate-500">FCFA/unité</div>
                  </td>
                  <td className="p-4 text-center">
                    <div className="flex items-center justify-center gap-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => openEditModal(prod)}
                        className="text-blue-500 hover:text-blue-700 hover:bg-blue-50"
                        data-testid={`edit-production-${prod.id}`}
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setProductionToDelete(prod)}
                        className="text-red-500 hover:text-red-700 hover:bg-red-50"
                        data-testid={`delete-production-${prod.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {productions.length === 0 && (
            <div className="text-center py-12 text-slate-500">
              Aucune production enregistrée
            </div>
          )}
        </div>
      </Card>

      {/* Edit Production Modal */}
      <Dialog open={showEditProductionModal} onOpenChange={setShowEditProductionModal}>
        <DialogContent className="sm:max-w-[450px]" data-testid="edit-production-modal">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-[#1B3268] flex items-center gap-2">
              <Edit className="w-6 h-6" />
              Modifier la Production
            </DialogTitle>
            <DialogDescription>
              Modifiez les informations de cette production.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Date */}
            <div className="space-y-2">
              <Label htmlFor="edit-date" className="text-sm font-medium text-slate-700">
                <Calendar className="w-4 h-4 inline mr-1" />
                Date de production
              </Label>
              <Input
                id="edit-date"
                type="date"
                value={editForm.date}
                onChange={(e) => setEditForm({...editForm, date: e.target.value})}
                data-testid="edit-production-date"
                className="border-slate-300"
              />
            </div>

            {/* Type d'agglo */}
            <div className="space-y-2">
              <Label htmlFor="edit-aggloType" className="text-sm font-medium text-slate-700">
                <Package className="w-4 h-4 inline mr-1" />
                Type d'Agglo
              </Label>
              <Select 
                value={editForm.aggloType} 
                onValueChange={(value) => setEditForm({...editForm, aggloType: value})}
              >
                <SelectTrigger data-testid="edit-production-agglo-type" className="border-slate-300">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="agglo_10">Agglo de 10</SelectItem>
                  <SelectItem value="agglo_15">Agglo de 15</SelectItem>
                  <SelectItem value="agglo_20">Agglo de 20</SelectItem>
                  <SelectItem value="agglo_hourdis_16">Agglo Hourdis 16</SelectItem>
                  <SelectItem value="agglo_plein_15">Agglo Plein 15</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Nombre d'agglos */}
            <div className="space-y-2">
              <Label htmlFor="edit-aggloCount" className="text-sm font-medium text-slate-700">
                Nombre d'Agglos produits *
              </Label>
              <Input
                id="edit-aggloCount"
                type="number"
                min="1"
                value={editForm.aggloCount}
                onChange={(e) => setEditForm({...editForm, aggloCount: e.target.value})}
                data-testid="edit-production-agglo-count"
                className="border-slate-300 text-lg font-medium"
              />
            </div>

            {/* Warning */}
            <div className="bg-amber-50 border border-amber-200 rounded-md p-3 flex items-start gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-amber-800">
                <p className="font-medium">Impact sur le stock</p>
                <p className="text-amber-700">La modification de la quantité ajustera automatiquement le stock de produits finis.</p>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowEditProductionModal(false);
                setEditingProduction(null);
              }}
              data-testid="cancel-edit-production"
            >
              Annuler
            </Button>
            <Button
              onClick={handleUpdateProduction}
              disabled={updating}
              data-testid="save-edit-production"
              className="bg-[#1B3268] hover:bg-[#152a55] text-white"
            >
              {updating ? 'Mise à jour...' : 'Enregistrer'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* New Production Modal - Simplified */}
      <Dialog open={showNewProductionModal} onOpenChange={setShowNewProductionModal}>
        <DialogContent className="sm:max-w-[500px]" data-testid="new-production-modal">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-[#1B3268] flex items-center gap-2">
              <Plus className="w-6 h-6" />
              Nouvelle Production
            </DialogTitle>
            <DialogDescription>
              Sélectionnez le type d'agglo et le nombre produit. Les matériaux seront calculés automatiquement selon la recette de référence.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Date */}
            <div className="space-y-2">
              <Label htmlFor="date" className="text-sm font-medium text-slate-700">
                <Calendar className="w-4 h-4 inline mr-1" />
                Date de production
              </Label>
              <Input
                id="date"
                type="date"
                value={newProduction.date}
                onChange={(e) => setNewProduction({...newProduction, date: e.target.value})}
                data-testid="new-production-date"
                className="border-slate-300"
              />
            </div>

            {/* Type d'agglo */}
            <div className="space-y-2">
              <Label htmlFor="aggloType" className="text-sm font-medium text-slate-700">
                <Package className="w-4 h-4 inline mr-1" />
                Type d'Agglo
              </Label>
              <Select 
                value={newProduction.aggloType} 
                onValueChange={handleAggloTypeChange}
              >
                <SelectTrigger data-testid="new-production-agglo-type" className="border-slate-300">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="agglo_10">Agglo de 10</SelectItem>
                  <SelectItem value="agglo_15">Agglo de 15</SelectItem>
                  <SelectItem value="agglo_20">Agglo de 20</SelectItem>
                  <SelectItem value="agglo_hourdis_16">Agglo Hourdis 16</SelectItem>
                  <SelectItem value="agglo_plein_15">Agglo Plein 15</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Warning if no template */}
            {!selectedTemplate && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-md p-3 flex items-start gap-2">
                <AlertTriangle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-yellow-800">
                  <p className="font-medium">Aucune recette de référence</p>
                  <p className="text-yellow-700">Veuillez d'abord créer une recette pour ce type d'agglo depuis la page Calculateur.</p>
                </div>
              </div>
            )}

            {/* Show template info if available */}
            {selectedTemplate && (
              <div className="bg-blue-50 border border-blue-200 rounded-md p-3">
                <p className="text-sm font-medium text-blue-800 mb-2">Recette de référence :</p>
                <div className="grid grid-cols-2 gap-2 text-xs text-blue-700">
                  <div>{getCementTypeLabel(selectedTemplate.cement_type)}: {selectedTemplate.cement_per_unit} kg/unité</div>
                  <div>Sable: {selectedTemplate.sand_per_unit} kg/unité</div>
                  {selectedTemplate.dune_sand_per_unit > 0 && (
                    <div>Gravier 2/4: {selectedTemplate.dune_sand_per_unit} kg/unité</div>
                  )}
                  <div>Gravier 3/8: {selectedTemplate.gravel_per_unit} kg/unité</div>
                </div>
                <p className="text-sm font-semibold text-blue-800 mt-2">
                  Coût unitaire: {selectedTemplate.cost_per_unit.toFixed(0)} FCFA
                </p>
              </div>
            )}

            {/* Nombre d'agglos produits */}
            <div className="space-y-2">
              <Label htmlFor="aggloCount" className="text-sm font-medium text-slate-700">
                Nombre d'Agglos produits *
              </Label>
              <Input
                id="aggloCount"
                type="number"
                min="1"
                placeholder="Ex: 100"
                value={newProduction.aggloCount}
                onChange={(e) => setNewProduction({...newProduction, aggloCount: e.target.value})}
                data-testid="new-production-agglo-count"
                className="border-slate-300 text-lg font-medium"
              />
            </div>

            {/* Estimation des matériaux */}
            {estimatedMaterials && (
              <div className="bg-slate-50 border border-slate-200 rounded-md p-4">
                <p className="text-sm font-semibold text-slate-700 mb-3">Estimation des matériaux utilisés :</p>
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-600">{getCementTypeLabel(selectedTemplate.cement_type)}</span>
                    <span className="font-mono font-medium">{estimatedMaterials.cement} kg</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-600">Sable</span>
                    <span className="font-mono font-medium">{estimatedMaterials.sand} kg</span>
                  </div>
                  {parseFloat(estimatedMaterials.duneSand) > 0 && (
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-600">Gravier 2/4</span>
                      <span className="font-mono font-medium">{estimatedMaterials.duneSand} kg</span>
                    </div>
                  )}
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-600">Gravier 3/8</span>
                    <span className="font-mono font-medium">{estimatedMaterials.gravel} kg</span>
                  </div>
                  <hr className="my-2" />
                  <div className="flex justify-between text-base font-bold">
                    <span className="text-slate-700">Coût Total Estimé</span>
                    <span className="text-[#1B3268]">{parseInt(estimatedMaterials.totalCost).toLocaleString('fr-FR')} FCFA</span>
                  </div>
                </div>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowNewProductionModal(false);
                resetNewProductionForm();
              }}
              data-testid="cancel-new-production"
            >
              Annuler
            </Button>
            <Button
              onClick={handleCreateNewProduction}
              disabled={saving || !selectedTemplate}
              data-testid="save-new-production"
              className="bg-[#1B3268] hover:bg-[#152a55] text-white"
            >
              {saving ? 'Enregistrement...' : 'Enregistrer Production'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!productionToDelete} onOpenChange={() => setProductionToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="text-red-600 flex items-center gap-2">
              <Trash2 className="w-5 h-5" />
              Supprimer cette production ?
            </AlertDialogTitle>
            <AlertDialogDescription>
              {productionToDelete && (
                <div className="space-y-2 mt-2">
                  <p>Vous êtes sur le point de supprimer la production suivante :</p>
                  <div className="bg-slate-50 p-3 rounded-md text-sm">
                    <p><strong>Type :</strong> {getAggloTypeLabel(productionToDelete.agglo_type)}</p>
                    <p><strong>Quantité :</strong> {productionToDelete.agglo_produced} unités</p>
                    <p><strong>Date :</strong> {new Date(productionToDelete.created_at).toLocaleDateString('fr-FR')}</p>
                  </div>
                  <p className="text-amber-600 font-medium mt-3">
                    ⚠️ Les matières premières utilisées seront restaurées dans le stock.
                  </p>
                </div>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Annuler</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteProduction}
              disabled={deleting}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              {deleting ? 'Suppression...' : 'Supprimer'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default ProductionPage;
